import { tss } from 'tss-react/mui';

import { Avatar, Chip, Tooltip } from '@mui/material';

import humanizeList from 'humanize-list';

import { graphql, type DocumentType } from '@/gql';
import { UserAvatar } from '@/modules/user/components/UserAvatar';
import { extractConnectionProps } from '@/utils/queries/queryExtracts';
import { nonFalsey } from '@/utils/nonFalsey';

const POST_NOTIFICATION_BAR_POST_FRAGMENT = graphql(`
  fragment postNotificationBarPostFragment on Post {
    _id
    subgroupStr
    subgroup {
      _id
    }
    notificationsConnection {
      edges {
        node {
          _id
          targetType
          targetName
          target {
            ... on Group {
              _id
            }
            ... on User {
              ...authorData
              ...userImageFields
            }
          }
        }
      }
    }
  }
`);

interface Props {
  post: DocumentType<typeof POST_NOTIFICATION_BAR_POST_FRAGMENT>;
  getSubgroupColor: (subgroupId: string) => string;
}

const useStyles = tss.create(({ theme }) => ({
  tooltip: {
    textAlign: 'center',
    maxWidth: 200,
    fontSize: theme.typography.caption.fontSize,
  },
  truncatedChip: {
    overflow: 'hidden',
    maxWidth: '18rem',
    marginRight: theme.spacing(1),
  },
  truncatedChipText: {
    display: 'block',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    maxWidth: '18rem',
  },
}));

export const PostNotificationBar = ({ post, getSubgroupColor }: Props) => {
  const { notificationsConnection, subgroupStr, subgroup } = post ?? {};

  const { classes } = useStyles();

  const subgroupColor = subgroup?._id
    ? getSubgroupColor(subgroup._id)
    : undefined;

  const notifications = extractConnectionProps(notificationsConnection);

  const getMoreEmailedUsersTooltip = () => {
    const names = notifications
      .map((notification) => notification.targetName)
      .filter(nonFalsey);

    return humanizeList(names) + ' were all sent emails';
  };

  const moreEmailedUsersTooltip = getMoreEmailedUsersTooltip();

  if (notifications.length > 0) {
    const firstNotification = notifications[0];
    const { target, targetName } = firstNotification;

    return (
      <div>
        <Chip
          className={classes.truncatedChip}
          classes={{ label: classes.truncatedChipText }}
          label={firstNotification.targetName}
          avatar={
            target?.__typename === 'User' ? (
              <UserAvatar user={target} size={24} />
            ) : target?.__typename === 'Group' ? (
              <Avatar>{targetName?.slice(0, 1).toUpperCase()}</Avatar>
            ) : undefined
          }
        />
        {notifications.length > 1 ? (
          <Tooltip
            placement="top"
            className={classes.tooltip}
            title={moreEmailedUsersTooltip}
          >
            <Chip
              className={`${classes.truncatedChip}`}
              classes={{ label: classes.truncatedChipText }}
              label={`+${notifications.length - 1} more`}
              color="primary"
            />
          </Tooltip>
        ) : null}
      </div>
    );
  }

  return (
    <Chip
      className={classes.truncatedChip}
      classes={{
        label: classes.truncatedChipText,
      }}
      label={subgroupStr ?? 'Everyone'}
      avatar={
        <Avatar
          style={{
            backgroundColor: subgroupColor ?? undefined,
            color: subgroupColor ? '#fff' : undefined,
          }}
        >
          {subgroupStr ? subgroupStr.charAt(0).toUpperCase() : 'E'}
        </Avatar>
      }
    />
  );
};
